import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Box,
  Divider,
} from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useHistory, NavLink, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useWorkplaceUser } from 'module/documents/utils';
import LogbookWordIcon from 'assets/images/LogbookWordIcons.png';
import { getEmpNoteById, postEmpNote, putEmpNote } from 'core/API/logbook';
import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useDocumentContext } from 'module/documents/context';
import { getEnrichedWorkplaceUsers } from 'core/utils/enrichWorkplaceUsers';

const AddEmployeeNotes = () => {
  const { showToast } = useToastContext();
  const { noteId } = useParams<{ noteId?: string }>();
  const history = useHistory();
  const workplace = useWorkplaceUser();
  const workplaceId = parseInt(localStorage.getItem('workplace-id') || '1');
  const { startDate } = useDocumentContext();

  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [employee, setEmployee] = useState<any>(null);
  const [employeeError, setEmployeeError] = useState('');

  // Use enriched workplace users (workplace_employees/users merged with /users)
  const { data: usersData } = useQuery(
    ['users-workplaces', workplaceId],
    () => getEnrichedWorkplaceUsers(workplaceId),
    {
      enabled: true,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );

  const { data: empNoteById } = useQuery(
    ['empNoteById', noteId],
    () => getEmpNoteById(parseInt(noteId as string, 10)),
    { enabled: !!noteId }
  );

  // Updated employee options using new API response structure
  const employeeOptions = useMemo(() => {
    if (!usersData) {
      return [];
    }
    console.log("usersData", usersData);

    // Filter out hidden users, admin users, and support users
    const filteredUsers = usersData.filter((user: any) => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
      const firstName = (user.firstName || '').toLowerCase();
      const lastName = (user.lastName || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      
      // Check security role if available
      const roleName = user?.securityRole?.name || 
                      user?._raw?.securityRole?.name || 
                      user?._raw?.security_role?.name || 
                      '';
      const hasRestrictedRole = roleName && [
        'Mabel Support', 
        'Mabel Admin', 
        'BYOD Support', 
        'BYOD Admin',
        'Admin User',
        'Support User'
      ].includes(roleName);
      
      // Filter out users with admin/support in name
      const isAdminUser = fullName.includes('admin user') || 
                         fullName === 'admin user' ||
                         (firstName === 'admin' && lastName === 'user');
      
      const isSupportUser = fullName.includes('support user') || 
                           fullName === 'support user' ||
                           (firstName === 'support' && lastName === 'user');
      
      // Filter out users with admin/support in email (common pattern)
      const hasAdminSupportEmail = email.includes('admin@') || 
                                  email.includes('support@') || 
                                  email.includes('@admin') || 
                                  email.includes('@support');
      
      // Filter out hidden users (check for common hidden user patterns)
      const isHiddenUser = fullName.includes('hidden') || 
                         email.includes('hidden') ||
                         firstName === 'hidden' ||
                         (user._raw && (user._raw.hidden === true || user._raw.isHidden === true));
      
      // Filter out separated users
      const isSeparated = user.separation === true || user._raw?.separation === true;
      
      return !hasRestrictedRole && 
             !isAdminUser && 
             !isSupportUser && 
             !hasAdminSupportEmail && 
             !isHiddenUser &&
             !isSeparated;
    });

    return filteredUsers
      .map((user: any) => {
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        const displayName = fullName || user.email || 'Unknown User';
        return {
          value: user.id,
          label: fullName || user.email,
          email: user.email,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
        };
      })
      .sort((a, b) => {
        // Sort by last name first, then by first name
        const lastNameA = a.lastName.toLowerCase();
        const lastNameB = b.lastName.toLowerCase();
        const firstNameA = a.firstName.toLowerCase();
        const firstNameB = b.firstName.toLowerCase();
        
        if (lastNameA !== lastNameB) {
          return lastNameA.localeCompare(lastNameB);
        }
        return firstNameA.localeCompare(firstNameB);
      });
  }, [usersData]);

  useEffect(() => {
    if (!noteId || !empNoteById) return;

    const noteContent = empNoteById.note || '';
    setTimeout(() => {
      setNote(noteContent);
    }, 100);

    if (empNoteById.author && typeof empNoteById.author === 'object') {
      const userId = empNoteById.author.id;
      const userOption = employeeOptions.find(option => option.value === userId);
      if (userOption) {
        setEmployee(userOption);
      }
    }
  }, [noteId, empNoteById, employeeOptions]);

  const { mutate: createNote, isLoading: isCreating } = useMutation(postEmpNote, {
    onSuccess: () => {
      showToast('success', 'Employee Note added!');
      history.push(ROUTES.LOGBOOK);
    },
    onError: () => {
      showToast('error', 'Failed to add Employee Note.');
    },
  });

  const { mutate: updateNote, isLoading: isUpdating } = useMutation(putEmpNote, {
    onSuccess: () => {
      showToast('success', 'Employee Note updated!');
      history.push(ROUTES.LOGBOOK);
    },
    onError: () => {
      showToast('error', 'Failed to update Employee Note.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let hasError = false;

    if (!employee) {
      setEmployeeError('Please select an employee');
      hasError = true;
    } else {
      setEmployeeError('');
    }

    if (!note.trim()) {
      setError('Employee note cannot be empty.');
      hasError = true;
    } else {
      setError('');
    }

    if (hasError) return;

    const timestamp = new Date().toISOString();

    // Convert startDate (YYYY-MM-DD) to ISO format with time
    // Same logic as used in sales/traffic
    let dateISO: string | undefined;
    if (startDate) {
      dateISO = new Date(startDate).toISOString();
    }

    // Prepare note content for submission
    const payload = {
      workplaceId,
      empName: employee.value, // Use the user ID instead of label
      note,
      timestamp,
      date: dateISO, // Add date from calendar
    };

    if (noteId) {
      updateNote({ ...payload, id: parseInt(noteId) });
    } else {
      createNote(payload);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3 }}>
      <Box maxWidth="1100px" mx="auto">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
          onClick={() => history.goBack()}
          sx={{
            backgroundColor: 'rgb(68, 99, 136)',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 0.75,
            '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
          }}
        >
          Back
        </Button>

        <Button
          type="submit"
          variant="contained"
          form="empNoteForm"
          sx={{
            backgroundColor: 'rgb(68, 99, 136)',
            color: '#fff',
            px: 4,
            textTransform: 'none',
            '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
          }}
          disabled={isCreating || isUpdating}
        >
          {(isCreating || isUpdating) ? 'Saving...' : 'Save'}
        </Button>
      </Box>

        <Paper elevation={1}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.100',
              borderBottom: 1,
              borderColor: 'divider',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight={600}>
                {noteId ? 'Edit Employee Note' : 'Add Employee Note'}
              </Typography>
                {/* <Button
                  type="submit"
                  variant="contained"
                  form="empNoteForm"
                  sx={{
                    backgroundColor: 'rgb(68, 99, 136)',
                    color: '#fff',
                    px: 4,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
                  }}
                  disabled={isCreating || isUpdating}
                >
                  {(isCreating || isUpdating) ? 'Saving...' : 'Save'}
                </Button> */}
            </Box>
          </Box>

          <Box sx={{ p: 3, minHeight: 350 }}>
            <form id="empNoteForm" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sx={{ zIndex: 10, position: 'relative' }}>
                  <Select
                    value={employee}
                    onChange={setEmployee}
                    options={employeeOptions}
                    placeholder="Select Employee"
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 }),
                      placeholder: base => ({ ...base, color: '#888' }),
                      control: base => ({ ...base, zIndex: 1 }),
                    }}
                  />
                  {employeeError && (
                    <Typography variant="caption" color="error">
                      {employeeError}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <ReactQuill
                    key={noteId || 'new'}
                    value={note}
                    onChange={(content) => {
                      // console.log("content changed", content);
                      setNote(content);
                    }}
                    placeholder="Enter note here"
                    modules={{ 
                      toolbar: [['bold'], [{ list: 'bullet' }]],
                      clipboard: {
                        matchVisual: false
                      }
                    }}
                    formats={['bold', 'list', 'bullet', 'indent', 'align', 'script', 'header']}
                    preserveWhitespace={true}
                    className="quill-editor"
                    style={{
                      backgroundColor: 'white',
                      height: '200px', // FIXED height
                      // overflowY: 'hide', // enable scroll inside
                    }}
                  />
                            {error && (
                    <Typography variant="caption" color="error">
                      {error}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
                {/* <Button
                  onClick={() => history.push(ROUTES.LOGBOOK)}
                  sx={{ color: '#7a1b8d', textTransform: 'none' }}
                >
                  Cancel
                </Button> */}
                {/* <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    backgroundColor: '#263238',
                    color: 'white',
                    px: 4,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#1a242b' },
                  }}
                  disabled={isCreating || isUpdating}
                >
                  {(isCreating || isUpdating) ? 'Saving...' : 'Save'}
                </Button> */}
              </Box>
            </form>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AddEmployeeNotes;